import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { AccessToCandidatePolicy, FullCandidateSelectors } from '@core/candidate-data';
import { InterviewTemplateSelectService } from '@core/interview-results-legacy';
import { InterviewTemplateScope } from '@core/interview-templates-data';
import { CandidateInterviewTemplatesService } from '@core/interview-templates-logic';
import { OfferKind } from '@core/offers-types';
import type { OfferSelectOption } from '@core/offers-ui';
import { deepEqual } from '@core/utils-legacy';

@Injectable()
export class RequestEvaluationDataService {
  private readonly store = inject(Store);
  private readonly interviewTemplateSelectService = inject(InterviewTemplateSelectService);
  private readonly candidateInterviewTemplatesService = inject(CandidateInterviewTemplatesService);

  public readonly currentOffer = signal<OfferSelectOption | undefined>(undefined);
  public readonly candidateId = signal<number | undefined>(undefined);
  public readonly isTalentPool = computed(() => this.currentOffer()?.kind === OfferKind.TalentPool);

  public readonly canManageInterviewTemplates = this.store.selectSignal(
    AbilitiesSelectors.hasOne(Ability.ManageInterviewTemplates),
  );
  public readonly canViewCandidates = this.store.selectSignal(
    AbilitiesSelectors.hasOne(Ability.ViewCandidates),
  );
  public readonly allGroupedEvaluationForms = toSignal(
    this.interviewTemplateSelectService.getInterviewTemplatesSelectOptions(
      InterviewTemplateScope.Interview,
    ),
    { initialValue: [] },
  );
  public readonly fetchPayloadData = computed(() => ({
    candidateId: this.candidateId(),
    offer: this.currentOffer(),
    allGroupedTemplates: this.allGroupedEvaluationForms(),
  }));
  public readonly templatesState = toSignal(
    toObservable(this.fetchPayloadData).pipe(
      distinctUntilChanged(deepEqual),
      switchMap(({ candidateId, offer, allGroupedTemplates }) => {
        const offerId = offer ? Number(offer.id) : null;

        if (!this.isTalentPool() && offerId && candidateId) {
          return combineLatest({
            result: this.candidateInterviewTemplatesService.fetchGroupedCandidateTemplates({
              candidateId,
              offerId,
              kind: 'interview',
            }),
            loaded: this.candidateInterviewTemplatesService.areCandidateTemplatesLoaded({
              candidateId,
              offerId,
              kind: 'interview',
            }),
          }).pipe(
            map(({ result, loaded }) => ({
              groups: result.groups,
              preselectedTemplateId: result.preselectedTemplateId,
              loaded,
            })),
          );
        }

        return of({ groups: allGroupedTemplates, preselectedTemplateId: null, loaded: true });
      }),
    ),
    { initialValue: { groups: [], preselectedTemplateId: null, loaded: false } },
  );

  public readonly visibleTemplates = computed(() => this.templatesState().groups);
  public readonly templatesLoaded = computed(() => this.templatesState().loaded);
  public readonly preselectedTemplateId = computed(
    () => this.templatesState().preselectedTemplateId,
  );

  public readonly groupedUsers = toSignal(
    toObservable(
      computed(() => ({ candidateId: this.candidateId(), offer: this.currentOffer() })),
    ).pipe(
      distinctUntilChanged(deepEqual),
      switchMap(({ candidateId, offer }) => {
        const offerId = offer ? Number(offer.id) : null;

        return this.store.select(
          FullCandidateSelectors.groupedUsersInShareActionContext({
            candidateId,
            ...(this.isTalentPool() ? { talentPoolId: offerId } : { offerId }),
            atcPolicy: AccessToCandidatePolicy.Offer,
          }),
        );
      }),
    ),
  );
  public readonly allowedUsers = computed(() => this.groupedUsers()?.usersWithAccess ?? []);
  public readonly disallowedUsers = computed(() => this.groupedUsers()?.usersWithoutAccess ?? []);
}
